// src/components/copiloto/CopilotoOrganizar.js — card de etiqueta/pasta (fase 4).
//
// Out/26. "Escrita reversível" do documento: até 50 contatos a ação JÁ foi
// aplicada quando este card aparece, e ele oferece "Desfazer" por 10
// segundos, com a barra de progresso do design. Acima de 50 o card chega
// pendente e pede confirmação antes — depois de confirmado, mesmo Desfazer.
//
// A contagem regressiva parte do `segundos_para_desfazer` que o SERVIDOR
// calculou, não do relógio do computador da pessoa (que pode estar
// adiantado e esconder um Desfazer ainda válido). O servidor aceita alguns
// segundos a mais que a tela, pela rede.
import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { FiTag, FiFolder, FiCheck } from 'react-icons/fi';
import api from '../../services/Api';
import { C, FONT_NUM, FONT_TXT, ACENTO, T } from './tema';

const AMBAR = T.ambar;

// A mesma paleta das etiquetas da tela de Contatos (green/blue/purple/red/
// amber/pink), em versão pro fundo escuro.
const CORES = {
  green: '#34D399', blue: '#60A5FA', purple: '#A78BFA',
  red: '#F87171', amber: '#FBBF24', pink: '#F472B6',
};

const focoVisivel = { '&:focus-visible': { outline: `2px solid ${ACENTO.base}`, outlineOffset: 2 } };

function Pilula({ nome, cor, pasta }) {
  const c = pasta ? ACENTO.claro : (CORES[cor] || CORES.green);
  return (
    <Box component="span" sx={{
      display: 'inline-block', lineHeight: 1.5,
      fontSize: '9.92px', fontWeight: 700, p: '0 7px', borderRadius: '6px', whiteSpace: 'nowrap',
      bgcolor: `${c}1a`, color: c, border: `1px solid ${c}4d`,
    }}>{nome}</Box>
  );
}

const plural = (n, um, varios) => `${n.toLocaleString('pt-BR')} ${n === 1 ? um : varios}`;

// A frase do card, por tipo e operação, em três tempos: `pendente` fala do
// que VAI acontecer (no card de confirmação nada mudou ainda — o e2e pegou
// "aplicada a 60 contatos" ao lado de "Aguardando você"), `feita` do que
// aconteceu, `desfeita` do que NÃO aconteceu.
function frase(a, estado) {
  const n = a.contatos || 0;
  const alvo = <Pilula nome={a.alvo?.nome} cor={a.alvo?.cor} pasta={a.tipo === 'pasta'} />;
  const origem = <Pilula nome={a.origem?.nome} pasta />;
  if (a.tipo === 'etiqueta') {
    const remove = a.operacao === 'remove';
    if (estado === 'pendente') {
      return remove
        ? <>Tirar a etiqueta {alvo} de {plural(n, 'contato', 'contatos')}</>
        : <>Aplicar a etiqueta {alvo} a {plural(n, 'contato', 'contatos')}</>;
    }
    if (estado === 'desfeita') {
      return remove
        ? <>Desfeito — a etiqueta {alvo} voltou para {plural(n, 'contato', 'contatos')}.</>
        : <>Desfeito — a etiqueta {alvo} não foi aplicada.</>;
    }
    return remove
      ? <>Etiqueta {alvo} retirada de {plural(n, 'contato', 'contatos')}</>
      : <>Etiqueta {alvo} aplicada a {plural(n, 'contato', 'contatos')}</>;
  }
  if (estado === 'desfeita') return <>Desfeito — os contatos voltaram para onde estavam.</>;
  if (estado === 'pendente') {
    if (a.operacao === 'move') return <>Mover {plural(n, 'contato', 'contatos')} de {origem} para {alvo}</>;
    if (a.operacao === 'remove') return <>Tirar {plural(n, 'contato', 'contatos')} da pasta {alvo}</>;
    return <>Colocar {plural(n, 'contato', 'contatos')} na pasta {alvo}</>;
  }
  if (a.operacao === 'move') {
    return <>{plural(n, 'contato movido', 'contatos movidos')} de {origem} para {alvo}</>;
  }
  if (a.operacao === 'remove') return <>{plural(n, 'contato tirado', 'contatos tirados')} da pasta {alvo}</>;
  return <>{plural(n, 'contato colocado', 'contatos colocados')} na pasta {alvo}</>;
}

function rotuloConfirmar(a) {
  const n = (a.contatos || 0).toLocaleString('pt-BR');
  if (a.tipo === 'etiqueta') return a.operacao === 'remove' ? `Tirar de ${n} contatos` : `Aplicar a ${n} contatos`;
  if (a.operacao === 'move') return `Mover ${n} contatos`;
  if (a.operacao === 'remove') return `Tirar ${n} contatos da pasta`;
  return `Colocar ${n} contatos na pasta`;
}

export default function CopilotoOrganizar({ acao: inicial }) {
  const [acao, setAcao] = useState(inicial);
  const [restantes, setRestantes] = useState(inicial.segundos_para_desfazer || 0);
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState('');

  const janela = acao.janela_segundos || 10;
  const executada = acao.status === 'executed';
  const podeDesfazer = executada && restantes > 0;

  useEffect(() => {
    if (!executada || restantes <= 0) return undefined;
    const t = setTimeout(() => setRestantes((r) => Math.max(0, r - 1)), 1000);
    return () => clearTimeout(t);
  }, [executada, restantes]);

  const receber = (nova) => {
    setAcao(nova);
    setRestantes(nova.segundos_para_desfazer || 0);
  };

  const chamar = async (rota, corpo) => {
    if (ocupado) return;
    setOcupado(true);
    setAviso('');
    try {
      const { data } = await api.post(`/copilot/actions/${acao.acao_id}/${rota}`, corpo);
      receber(data.acao);
    } catch (e) {
      const d = e?.response?.data;
      if (d?.acao) receber(d.acao);
      if (e?.response?.status === 410) setRestantes(0);
      setAviso(d?.detail || (e?.response ? 'Não foi possível concluir.' : 'Sem conexão com o servidor.'));
    } finally {
      setOcupado(false);
    }
  };

  const Icone = acao.tipo === 'pasta' ? FiFolder : FiTag;
  const neutro = ['undone', 'cancelled', 'expired', 'failed'].includes(acao.status);

  // ── Pendente: acima de 50 contatos, confirma antes ───────────────────
  if (acao.status === 'pending') {
    return (
      <Box role="group" aria-label="Organizar contatos" sx={{ maxWidth: 560, bgcolor: C.bg, border: `1px solid ${T.cardBorda}`, borderRadius: '10px', overflow: 'hidden' }}>
        {/* No celular o selo desce pra linha de baixo, alinhado ao texto: lado
            a lado, ele espremia a frase em três linhas. */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: '10px', rowGap: '8px', p: '12px 14px', borderBottom: `1px solid ${T.linha('rgba(255,255,255,0.06)')}` }}>
          <Box sx={{ width: 28, height: 28, borderRadius: '7px', bgcolor: T.ambarBg, border: `1px solid ${T.ambarB}`, color: AMBAR, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icone size={14} />
          </Box>
          <Box sx={{ flex: 1, minWidth: { xs: 'calc(100% - 44px)', sm: 0 }, fontSize: '12.48px', color: C.text, lineHeight: 1.6 }}>
            {frase(acao, 'pendente')}
          </Box>
          <Box component="span" sx={{ ml: { xs: '38px', sm: 0 }, fontSize: '9.28px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', p: '3px 8px', borderRadius: '20px', color: AMBAR, bgcolor: T.ambarBg, border: `1px solid ${T.ambarB}`, whiteSpace: 'nowrap' }}>
            Aguardando você
          </Box>
        </Box>
        <Box sx={{ p: '10px 14px', fontSize: '10.56px', color: C.text2, lineHeight: 1.5 }}>
          {acao.filtro}{acao.ja_estavam ? ` · ${plural(acao.ja_estavam, 'já estava assim', 'já estavam assim')}` : ''}
        </Box>
        {aviso && <Box role="alert" sx={{ px: '14px', pb: '10px', fontSize: '10.88px', color: AMBAR }}>{aviso}</Box>}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '10px 14px', borderTop: `1px solid ${T.linha('rgba(255,255,255,0.06)')}`, bgcolor: T.rodape, flexWrap: 'wrap' }}>
          <Box component="span" sx={{ flex: { xs: '1 1 100%', sm: 1 }, fontSize: '10.56px', color: C.text2 }}>
            Mais de 50 contatos: nada muda antes da sua confirmação.
          </Box>
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Box component="button" type="button" disabled={ocupado} onClick={() => chamar('cancel')} sx={{
              fontFamily: FONT_TXT, fontSize: '11.2px', fontWeight: 600, color: C.text2, bgcolor: 'transparent',
              border: `1px solid ${C.border}`, borderRadius: '6px', p: '6.4px 12px', cursor: 'pointer',
              '&:hover': { color: C.text, bgcolor: T.hover }, ...focoVisivel,
            }}>Cancelar</Box>
            <Box component="button" type="button" disabled={ocupado} onClick={() => chamar('confirm', { contatos_vistos: acao.contatos })} sx={{
              fontFamily: FONT_TXT, fontSize: '11.2px', fontWeight: 700, color: T.sobreVerde, bgcolor: C.green,
              border: 'none', borderRadius: '6px', p: '7.4px 13px', cursor: 'pointer', whiteSpace: 'nowrap',
              '&:hover': { bgcolor: T.verdeHover }, ...focoVisivel,
            }}>{ocupado ? 'Aplicando…' : rotuloConfirmar(acao)}</Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Aplicada (com Desfazer), desfeita, ou encerrada ───────────────────
  const corIcone = neutro ? C.text2 : C.green;
  return (
    <Box role="group" aria-label="Organizar contatos" sx={{ maxWidth: 560, bgcolor: C.bg, border: `1px solid ${T.cardBorda}`, borderRadius: '10px', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '11px 14px' }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: '7px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: neutro ? T.hover : T.verdeBg,
          border: `1px solid ${neutro ? T.cardBorda : T.verdeB('rgba(0,214,124,0.22)')}`, color: corIcone,
        }}>
          {executada && !podeDesfazer ? <FiCheck size={14} /> : <Icone size={14} />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: '12.48px', lineHeight: 1.6, color: neutro ? C.text2 : C.text }}>
            {acao.status === 'undone' ? frase(acao, 'desfeita')
              : acao.status === 'cancelled' ? 'Cancelado. Nada mudou.'
                : acao.status === 'expired' ? 'Este card expirou sem confirmação. Nada mudou.'
                  : acao.status === 'failed' ? 'Não foi possível aplicar. Nada mudou.'
                    : frase(acao, 'feita')}
          </Box>
          <Box sx={{ fontSize: '10.56px', color: C.text2, mt: '2px' }}>
            {acao.erro || [acao.filtro, acao.ja_estavam && executada ? plural(acao.ja_estavam, 'já estava assim', 'já estavam assim') : '']
              .filter(Boolean).join(' · ')}
          </Box>
          {aviso && !podeDesfazer && <Box role="alert" sx={{ fontSize: '10.56px', color: AMBAR, mt: '2px' }}>{aviso}</Box>}
        </Box>
        {podeDesfazer && (
          <Box
            component="button" type="button" onClick={() => chamar('undo')} disabled={ocupado}
            sx={{
              display: 'flex', alignItems: 'center', gap: '6px', fontFamily: FONT_TXT, fontSize: '11.2px', fontWeight: 700,
              color: ACENTO.claro, bgcolor: 'transparent', border: '1px solid rgba(145,132,217,0.35)', borderRadius: '6px',
              p: '5.6px 11px', cursor: 'pointer', whiteSpace: 'nowrap', '&:hover': { bgcolor: 'rgba(145,132,217,0.12)' }, ...focoVisivel,
            }}
          >
            {ocupado ? 'Desfazendo…' : 'Desfazer'}
            <Box component="span" sx={{ fontFamily: FONT_NUM, fontWeight: 500, color: C.text2 }}>{restantes}s</Box>
          </Box>
        )}
      </Box>
      {podeDesfazer && (
        <Box sx={{ height: '2px', bgcolor: 'rgba(145,132,217,0.10)' }}>
          <Box sx={{ height: '100%', width: `${(restantes / janela) * 100}%`, bgcolor: ACENTO.base, transition: 'width 1s linear' }} />
        </Box>
      )}
    </Box>
  );
}
